/**
 * Vending Fleet Tour — client-side fallback steps (v2.6.2).
 *
 * Rewritten for a hybrid investor + vending-operator audience so both cohorts
 * walk away understanding EXACTLY what the module does. Every step maps to a
 * shipped feature — no more Sprint 1/2/3 language.
 *
 * Also mirrored in backend/routes/tour.py VENDING_TOUR_STEPS. If you edit one,
 * edit the other so live users and stale-Plesk users see identical narration.
 */
export const VENDING_TOUR_STEPS_FALLBACK = [
  {
    id: 'vending_intro',
    title: 'Welcome to the Vending Fleet',
    target: "[data-testid='vending-fleet-page']",
    page: '/vending',
    position: 'center',
    narration: "Welcome to Konnect's Vending module — a complete operating system for automated retail. Every machine, every location, every SKU, every cash and cashless transaction lives in one live dashboard. Whether you run five snack machines in an office park or five hundred combo units across a city, this is where you run the business.",
  },
  {
    id: 'vending_fleet_stats',
    title: 'Fleet Health at a Glance',
    target: "[data-testid='vending-fleet-stats']",
    page: '/vending',
    position: 'bottom',
    narration: "Your four topline KPIs — total machines, how many are deployed and earning, how many are sitting in the warehouse waiting to be placed, and how many client locations you're serving. Deploy rate is your fastest lever on revenue, so we surface it right at the top.",
  },
  {
    id: 'vending_machines_tab',
    title: 'Machines — Your Assets',
    target: "[data-testid='tab-machines']",
    page: '/vending',
    position: 'bottom',
    narration: "Every physical asset in your fleet, one card each. Chassis serial, current status — deployed, warehouse, servicing or decommissioned — the vendor, the model, the telemetry provider like Jetinno or Aurency, the operating currency, and the exact site it's placed at right now. Tap a card to open the full asset ledger with capital cost, deployment history, service records and lifetime revenue.",
  },
  {
    id: 'vending_add_machine',
    title: 'Add a Machine in Seconds',
    target: "[data-testid='vending-add-machine']",
    page: '/vending',
    position: 'left',
    narration: "Adding a machine pulls from a catalog of 32 pre-configured models — Jetinno, Necta, FAS, Bianchi, TCN, Crane, AMS, Sielaff, Aurency and more. Slots and default planograms auto-populate the moment you pick a model, so onboarding a new unit is a 30-second job instead of a data-entry marathon.",
  },
  {
    id: 'vending_locations_tab',
    title: 'Locations — Where the Money Is Made',
    target: "[data-testid='tab-locations']",
    page: '/vending',
    position: 'bottom',
    narration: "Every physical site your machines earn from — office parks, hospitals, factories, schools, gyms. Geolocation coordinates power route optimization. Site-type informs planogram recommendations. The primary contact even carries a birthday field the Retention module uses to auto-send a branded happy-birthday note every year.",
  },
  {
    id: 'vending_clients_tab',
    title: 'Clients — Your Host Companies',
    target: "[data-testid='tab-clients']",
    page: '/vending',
    position: 'bottom',
    narration: "The CRM of the vending world. Each host company carries six named contacts — Primary, AP, AR, Facilities, Decision Maker, Escalation — plus two banking blocks so Konnect knows exactly where commission payouts go and where invoices get collected. Paying-vend, freevend and hybrid billing models are all handled automatically.",
  },
  {
    id: 'vending_products_tab',
    title: 'Products — Your SKU Catalog',
    target: "[data-testid='tab-products']",
    page: '/vending',
    position: 'bottom',
    narration: "Every SKU in one catalog — from a bag of chips to a cappuccino recipe. Unit-based SKUs decrement by one per vend. Recipe-based SKUs like coffee deduct fractional ingredients from onboard canisters using a Bill of Materials. Enable expiration tracking on any product and Konnect enforces First-In-First-Out at every restock so you never bin stock again.",
  },
  {
    id: 'vending_add_product',
    title: 'Cost, Retail and Margin',
    target: "[data-testid='vending-add-product']",
    page: '/vending',
    position: 'left',
    narration: "Enter cost and retail price and Konnect computes gross margin instantly. Live telemetry then folds in commission paid to the host, driver visit cost, hardware amortization and telemetry fees, so every machine has a true per-vend and per-month profit number — the metric operators actually run the business on.",
  },
  {
    id: 'vending_dex_companion',
    title: 'DEX Companion — Pocket-Sized Ops',
    target: "[data-testid='quick-action-dex-companion']",
    page: '/vending',
    position: 'bottom',
    narration: "The DEX Companion is your driver's mobile cockpit. Scan a machine's QR code and instantly get its planogram, last DEX read, sales since last visit, missing tallies and prompted actions. Drivers can DEX-pull, capture cash counts, log service issues and confirm restocks — all offline-tolerant, syncing the second they hit signal.",
  },
  {
    id: 'vending_planogram',
    title: 'Visual Planogram Editor',
    target: "[data-testid='quick-action-planogram']",
    page: '/vending',
    position: 'bottom',
    narration: "A drag-and-drop, slot-by-slot planogram editor. Assign SKUs, set par levels, price per slot, spring size and rotation direction. Save a planogram once and apply it to every machine of the same model, so a fleet of 200 machines takes minutes to standardize instead of days.",
  },
  {
    id: 'vending_routing',
    title: 'Routing & Pre-Kitting',
    target: "[data-testid='quick-action-routing']",
    page: '/vending',
    position: 'bottom',
    narration: "The routing engine consumes real telemetry, days-until-sold-out forecasts, geolocations and driver schedules to produce optimized daily routes. Each route ships with a pre-kit — the exact SKU picklist a driver loads into the van before leaving the warehouse. Fewer stops, zero forgotten SKUs, lower fuel cost, higher fill rates.",
  },
  {
    id: 'vending_reconciliation',
    title: 'Cash Reconciliation',
    target: "[data-testid='quick-action-reconcile']",
    page: '/vending',
    position: 'bottom',
    narration: "Every driver visit produces two totals: telemetry says the machine should have X, driver counted Y. Konnect surfaces the variance instantly, flags outliers, and files a signed reconciliation record. Shrinkage becomes visible, not hidden — a compliance and audit win that pays for the module by itself.",
  },
  {
    id: 'vending_commissions',
    title: 'Commissions — Fully Automated',
    target: "[data-testid='quick-action-commissions']",
    page: '/vending',
    position: 'bottom',
    narration: "Each client contract stores its commission structure — flat rate, tiered by revenue, per-category, or hybrid. Vends flow in from telemetry, Konnect calculates what the host is owed and what your route techs are owed, generates the payout batch, and pushes to Payroll. What used to be a monthly spreadsheet ordeal is now a click.",
  },
  {
    id: 'vending_contracts',
    title: 'Client Contracts & E-Sign',
    target: "[data-testid='quick-action-contracts']",
    page: '/vending',
    position: 'bottom',
    narration: "Contracts are generated straight from the client and location profile — start date, term, commission rules, service SLA, exclusivity, machine list. E-signature lives in-app, so a new site can go from handshake to legally binding in the same visit. Every renewal, expiry and price-review lands in the calendar automatically.",
  },
  {
    id: 'vending_kiosk_tv',
    title: 'Kiosk TV — The Warehouse Wall Dashboard',
    target: "[data-testid='quick-action-kiosk-tv']",
    page: '/vending',
    position: 'bottom',
    narration: "Kiosk TV is a fullscreen, always-on dashboard designed for a monitor on your warehouse or ops-room wall. It rotates through low-stock alerts, flagged machines and telemetry sync health so the whole team sees priorities the moment they walk in — no logins, no clicks, just situational awareness.",
  },
  {
    id: 'vending_closing',
    title: "That's the full loop",
    target: "[data-testid='vending-title']",
    page: '/vending',
    position: 'center',
    narration: "Machines, locations, clients, SKUs, DEX Companion, planogram editor, routing, cash reconciliation, commissions, contracts and Kiosk TV — one integrated stack. It replaces the spreadsheets, the disconnected telemetry portals, the reconciliation binders and the commission consultants operators historically stitched together. This is unattended retail, finally run like software.",
  },
];
